import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { Stack } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { styled } from '@mui/material/styles';
import { Container } from './styles';
import { drawerBackground, primary } from '@/theme/colors';
import { DeckPlanOverlay } from '@/components/Analysis/DeckPlan/DeckPlanOverlay/DeckPlanOverlay';
import { DeckPlanCustomizationPanel } from '@/components/Analysis/DeckPlan/DeckPlanCustomizationPanel/DeckPlanCustomizationPanel';
import { FilterSideBar } from '@/components/Analysis/shared/FilterSideBar/FilterSideBar';
import { DeckList } from '@/components/shared/DeckList';
import { SideDrawer } from '@/components/shared/SideDrawer';
import {
  Maybe,
  useAssembliesDecksSummaryLazyQuery,
  DeckAreaSummary,
  useAllPointOfInterestsForDecksLazyQuery,
} from '@/__generated__/graphql';
import * as globalState from '@/state';
import {
  selectedAssemblyTemplateId,
  applyFilters as applyFiltersState,
  selectedTemplateFiltersState,
} from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { getPoiCoordinates } from '@/utils/pointOfInterest';
import { TemplateWithPOIs } from '@/types';
import { useFilterSyncWithParms } from '@/hooks/useFilterSyncWithParms';

const DeckPlanContainer = styled('main', { shouldForwardProp: (property) => property !== 'open' })<{
  open?: boolean;
  navbarHeight: number;
}>(({ theme, open, navbarHeight }) => ({
  overflowY: 'auto',
  height: `calc(100vh  / var(--zoom) - ${navbarHeight}px)`,
  flexGrow: 1,
  paddingTop: 60,
  padding: 32,
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

type Deck = {
  id: string;
  resourcePath: string;
  name: string;
  heightRange: number[];
  heightStep?: Maybe<number> | undefined;
};

export const DeckPlanExplorer = () => {
  const [open, setOpen] = useState(true);
  const [selectedDeck, setSelectedDeck] = useState<Deck | null>();
  const [loading, setLoading] = useState(false);

  const [isQueryCleared, setIsQueryCleared] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);

  const structureId = useGetStructureId();
  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const selectedTemplateId = useAtomValue(selectedAssemblyTemplateId) ?? '';
  const setStatesLoaded = useSetAtom(globalState.statesLoaded);
  const [applyFilters, setApplyFilters] = useAtom(applyFiltersState);

  const { leftDrawerWidth, rightDrawerWidth } = useDrawerWidths();
  const navbarHeight = useNavbarHeight();
  useFilterSyncWithParms();
  useEffect(() => {
    setApplyFilters(true);
    setStatesLoaded(false);
  }, [setStatesLoaded, setApplyFilters]);

  const [getFilteredAssemblyDecks, { data }] = useAssembliesDecksSummaryLazyQuery();

  useEffect(() => {
    if (selectedTemplateId && structureId && applyFilters) {
      getFilteredAssemblyDecks({
        variables: {
          input: {
            structureId,
            selectedTemplateFilters,
            templateId: selectedTemplateId,
          },
        },
      }).then(() => {
        setApplyFilters(false);
      });
    }
  }, [
    applyFilters,
    getFilteredAssemblyDecks,
    selectedTemplateFilters,
    selectedTemplateId,
    setApplyFilters,
    structureId,
  ]);

  const filteredAssemblyDecks = useMemo(() => {
    return data?.getAssembliesDecksSummary;
  }, [data?.getAssembliesDecksSummary]);

  useEffect(() => {
    const deckIds = filteredAssemblyDecks?.map((deck) => deck?.deckId);
    if (deckIds && deckIds?.length > 0) {
      setIsButtonDisabled(!deckIds?.includes(selectedDeck?.id));
    } else {
      setIsButtonDisabled(!!deckIds);
    }
  }, [filteredAssemblyDecks, selectedDeck]);

  const [getAllPointOfInterestData, { data: pointOfInterestData, loading: poisLoading }] =
    useAllPointOfInterestsForDecksLazyQuery();

  useEffect(() => {
    if (applyFilters) {
      getAllPointOfInterestData({
        variables: {
          structureId,
          selectedTemplateFilters,
        },
      });
    }
  }, [
    applyFilters,
    getAllPointOfInterestData,
    selectedTemplateFilters,
    selectedTemplateId,
    structureId,
  ]);

  const allTemplatesWithPOIs = useMemo(() => {
    const { allPointOfInterestTemplate = [], allPointOfInterests = [] } = pointOfInterestData ?? {};

    const data = allPointOfInterestTemplate.map(({ id: templateId, name, type }) => ({
      id: templateId,
      name,
      type,
      pointOfInterests: allPointOfInterests
        .filter(
          ({ template, paintRegion }) =>
            template?.id === templateId &&
            paintRegion?.deck?.id === selectedDeck?.id &&
            paintRegion?.deck?.cadTransform
        )
        .map(({ paintRegion, centerPoint3d }) => {
          const { cadTransform } = paintRegion!.deck!;
          const location = getPoiCoordinates(centerPoint3d, cadTransform);
          return { location, centerPoint3d };
        }),
    }));

    return data.map((template) => ({
      ...template,
      pointOfInterests: template.pointOfInterests.filter(
        // filtering out CSV uploaded POIs
        (poi) => poi.centerPoint3d !== undefined
      ),
    })) as TemplateWithPOIs[];
  }, [pointOfInterestData, selectedDeck?.id]);

  const handleDrawerToggle = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const handleSelectedDeck = useCallback(
    (item: Deck) => {
      setSelectedDeck(item);
      setImageError(false);
    },
    [setSelectedDeck]
  );

  const handleFilterCleared = useCallback(() => {
    setIsQueryCleared(true);
    setTimeout(() => {
      setIsQueryCleared(false);
    }, 1000);
  }, []);

  const fallbackImageHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('DeckPlanExplorer.tsx -> fallbackImageHandler() ->', event);
    const target = event.target as HTMLImageElement;
    target.src = '/assets/fallback_deckplan_overlay.png';
    setImageError(true);
  };

  return (
    <Container>
      <Stack direction="row">
        <Stack sx={{ width: `calc(100vw - ${rightDrawerWidth}px)` }}>
          <SideDrawer
            isOpen={open}
            handleOpen={handleDrawerToggle}
            {...(open ? {} : { icon: <TuneIcon /> })}
            position="left"
            width={leftDrawerWidth}
            label="Query"
            iconStyles={{ top: 10 }}
            showCloseButton
            allDrawersClosed={!open}
            sx={{
              width: leftDrawerWidth,
              flexShrink: 0,
            }}
          >
            <FilterSideBar onClear={handleFilterCleared} showDynamicFilters={false} />
          </SideDrawer>

          <DeckPlanContainer
            open={open}
            sx={{ marginLeft: open ? `${leftDrawerWidth}px` : '50px' }}
            navbarHeight={navbarHeight}
          >
            <DeckList
              selectedDeck={selectedDeck}
              handleSelection={handleSelectedDeck}
              loading={loading}
              handleSetLoading={setLoading}
              filteredAssemblyDecks={filteredAssemblyDecks as DeckAreaSummary[]}
              selectDefaultDeck
            />
            {selectedDeck?.id && selectedDeck?.resourcePath && (
              <DeckPlanOverlay
                loading={loading}
                deckId={selectedDeck.id}
                deckName={selectedDeck.name}
                resourcePath={selectedDeck.resourcePath}
                handleSetLoading={setLoading}
                handleFallbackImage={fallbackImageHandler}
                imageError={imageError}
              />
            )}
          </DeckPlanContainer>
        </Stack>

        <Stack
          sx={{
            borderWidth: 0,
            borderLeftWidth: 2,
            borderStyle: 'solid',
            borderColor: primary.light,
            backgroundColor: drawerBackground,
            padding: '0px !important',
            width: rightDrawerWidth,
          }}
        >
          <Stack
            sx={{
              margin: 0,
            }}
          >
            {selectedDeck && selectedDeck?.id?.length > 0 && (
              <DeckPlanCustomizationPanel
                selectedDeck={selectedDeck}
                isQueryCleared={isQueryCleared}
                isButtonDisabled={isButtonDisabled}
                allTemplatesWithPOIs={allTemplatesWithPOIs}
                poisLoading={poisLoading}
                imageError={imageError}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
